import { Dispatch } from "redux";
import { Location, NavigateFunction } from "react-router-dom";
import fetch from "cross-fetch";
import { datadogRum } from "@datadog/browser-rum";
import { LocaleTypes, RequestTypes } from "@iddataweb/idw-common-ui/enums";
import { AxnError } from "@iddataweb/idw-common-ui/utils";
import { EMAIL_METHOD, PHONE_METHOD } from "@constants/form-constants";
import { setShouldRedirect } from "@redux/actions/common-actions";
import { setError } from "@redux/actions/error-actions";
import { setAsyncMethod } from "@redux/actions/verification-form-actions";
import { hasEmail } from "@utils/verification-form-util";
import { Logger } from "@utils/logger";
import { buildSMSRequestParams } from "@utils/sms-util";
import { handleNavigationForError } from "@utils/navigation-util";
import { AttributeFormState } from "../types/state/attribute-form-state";

// https://test2.axn.iddataweb.com/test-axn-aws/axn/doc/sendasyncsms.max
// {"status":"success","errorMessage":""}
const sendStartAsyncSMS = async (requestParams: any = {}): Promise<any> => {
    const { url, ...postData } = requestParams;

    Logger.info(`Sending request for starting SMS at:  ${url}`);
    const request = {
        headers: {
            'Content-Type': 'application/json'
        },
        method: RequestTypes.POST,
        body: JSON.stringify(postData),
    };

    return fetch(url, request);
    // return fetch("http://localhost:3001/sendasyncsms")
};


const handleAsyncError = (dispatch: Dispatch) => (params: AxnError) => {
    dispatch(setError({
        name: params.name,
        message: params.message,
        asi: params.asi,
        errorCode: params.errorCode,
        errorURI: params.errorURI,
        httpStatus: params.httpStatus
    }));
};

const checkForSyncDCUI = (dispatch: Dispatch, navigate: NavigateFunction, location: Location) => (
    dcuiPreverify: boolean,
    syncDCUIRetryURL: string,
    asi: string,
    setSubmitting: (isSubmitting: boolean) => void,
    redirectToCallbackUrlOnError: boolean,
    oidcCallbackURL: string
) => {
    if (syncDCUIRetryURL) {
        dispatch(setShouldRedirect(true));
        Logger.info(`Redirecting triggered, ${syncDCUIRetryURL}`);
        setTimeout(() => {
            window.location.href = syncDCUIRetryURL;
        }, 500);
        return 'redirect';
    } else if (dcuiPreverify) {
        const error = {
            name: "DCUI Retry URL Error",
            message: 'Verification can not continue! Missing DCUI retry url',
            asi: asi,
            errorCode: "500",
            errorURI: window.location.href,
            httpStatus: "500",
            forwardedErrorCode: null as any,
            forwardedErrorDescription: null as any
        };

        dispatch(setError(error));
        handleNavigationForError(dispatch)(
            redirectToCallbackUrlOnError,
            oidcCallbackURL,
            asi,
            "/error",
            navigate,
            location.pathname,
            location.search,
            error
        );
        return 'error';
    }
    setSubmitting(false);
    return 'continue';
};

export const startAsyncDataCapture = (dispatch: Dispatch, navigate: NavigateFunction, location: Location) => async (
    dcuiPreverify: boolean,
    syncDCUIRetryURL: string,
    asi: string,
    attributeData: Array<AttributeFormState>,
    contextPath: string,
    setSubmitting: (isSubmitting: boolean) => void,
    redirectToCallbackUrlOnError: boolean,
    oidcCallbackURL: string,
    activeLanguage: LocaleTypes
): Promise<any> => {
    const syncDCUIResponse = checkForSyncDCUI(dispatch, navigate, location)(dcuiPreverify, syncDCUIRetryURL, asi, setSubmitting,
        redirectToCallbackUrlOnError, oidcCallbackURL);

    if (syncDCUIResponse === 'continue') {
        const foundEmail = hasEmail(attributeData);
        const asyncMethod = foundEmail ? EMAIL_METHOD : PHONE_METHOD;
        dispatch(setAsyncMethod(asyncMethod));
        const requestParams = buildSMSRequestParams(attributeData, asi, contextPath, activeLanguage);

        if (!requestParams) {
            const error: AxnError = {
                name: "Communication Error",
                asi,
                errorCode: "UI",
                message: "Communication Error. There is no way to continue the verification flow for this user",
                errorURI: window.location.href,
                httpStatus: "N/A"
            };
            throw error;
        } else {
            try {
                const response = await sendStartAsyncSMS(requestParams);
                if (!response.ok) {
                    const error: AxnError = {
                        name: "Async SMS Message Error",
                        asi,
                        errorCode: `${response.status}`,
                        message: `Error ${response.status}. There was a problem sending the Async SMS message: ${response.statusText}`,
                        errorURI: response.url,
                        httpStatus: `${response.status}`
                    };
                    throw error;
                }

                const json = await response.json();
                Logger.info('Async SMS success, now waiting for DCUI completion');
                if (json.status === 'success') {
                    Logger.info('Async SMS success, now waiting for DCUI completion');
                    return json;
                } else if (json.status === 'fail') {
                    Logger.error('Async SMS failure');
                    const error = {
                        name: 'Async SMS failure',
                        asi,
                        errorCode: json.errorCode || "UI",
                        message: json.errorMessage,
                        errorURI: window.location.href,
                        httpStatus: "N/A",
                        forwardedErrorCode: json.errorCode,
                        forwardedErrorDescription: json.errorMessage
                    };
                    throw error;
                } else {
                    const error = {
                        name: 'Async SMS failure',
                        asi,
                        errorCode: json.errorCode || "UI",
                        message: json.errorMessage,
                        errorURI: window.location.href,
                        httpStatus: "N/A",
                        forwardedErrorCode: json.errorCode,
                        forwardedErrorDescription: json.errorMessage
                    };
                    throw error;
                }
            } catch (error) {
                Logger.error(error);
                datadogRum.addError(error);
                handleAsyncError(dispatch)(error);
                handleNavigationForError(dispatch)(
                    redirectToCallbackUrlOnError,
                    oidcCallbackURL,
                    asi,
                    "/error",
                    navigate,
                    location.pathname,
                    location.search,
                    error
                );
            }
        }
    }
};